import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BadgeCheck, Instagram, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

const IgEmbed = ({ url }) => {
  const m = url.match(/instagram\.com\/(p|reel|tv)\/([\w-]+)/);
  if (!m) return null;
  return navigator.onLine
    ? <iframe data-testid="founder-page-ig" loading="lazy" title="Instagram post" scrolling="no" src={`https://www.instagram.com/${m[1]}/${m[2]}/embed/captioned/`} className="w-full h-[520px] rounded border border-stone-200 bg-white" />
    : <a href={url} target="_blank" rel="noreferrer" className="text-[#9A7B2C] font-semibold">✦ See my visit on Instagram</a>;
};

const Place = ({ p, i }) => (
  <article data-testid={`founder-place-${i}`} className="grid md:grid-cols-[1fr_360px] gap-8 py-10 border-t border-stone-200 rise" style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }}>
    <div>
      <div className="inline-flex items-center gap-1.5 rounded-full bg-[#2B2A26] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#E9D48F]"><BadgeCheck size={12} /> Founder Verified</div>
      <h3 className="font-display text-3xl text-[#2B2A26] mt-4">{p.name}</h3>
      {p.location && <div className="text-sm text-stone-500 mt-1 inline-flex items-center gap-1"><MapPin size={13} /> {p.location}</div>}
      {p.note && <p className="mt-4 text-stone-700 leading-relaxed font-[Gelasio,serif] italic">{p.note}</p>}
    </div>
    <div>{p.instagram ? <IgEmbed url={p.instagram} /> : <div className="h-full min-h-[120px] rounded border border-dashed border-stone-300 flex items-center justify-center text-sm text-stone-400 gap-2"><Instagram size={14} /> Post coming soon</div>}</div>
  </article>
);

export default function FounderPage() {
  const { slug } = useParams();
  const [k, setK] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { axios.get(`${API}/founders/${slug}`).then((r) => setK(r.data)).catch(() => setErr("This Founder's kit isn't available.")); }, [slug]);
  if (err) return <div data-testid="founder-page-error" className="min-h-screen flex items-center justify-center text-stone-500">{err}</div>;
  if (!k) return <div className="min-h-screen" />;
  let n = 0;
  const count = k.groups.reduce((a, g) => a + g.places.length, 0);
  return (
    <div data-testid="founder-page" className="min-h-screen bg-[#F7F5F0]">
      <header className="relative h-[70vh] min-h-[460px] bg-[#2B2A26] overflow-hidden">
        {k.has_cover && <img src={`${API}/products/${k.slug}/cover`} alt={k.title} className="absolute inset-0 w-full h-full object-cover opacity-70" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1d1c19] via-transparent to-transparent" />
        <div className="relative h-full max-w-6xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-14">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#E9D48F]">✦ Founder’s Collection · {k.edition_label}</div>
          <h1 data-testid="founder-page-title" className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-3 max-w-4xl">{k.destination}: the places I’ve been</h1>
          <p className="text-stone-200 mt-4 max-w-2xl">{k.intro || k.tagline}</p>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid lg:grid-cols-[1fr_300px] gap-12 mb-10">
          <div className="space-y-4 text-stone-700 leading-relaxed">{k.welcome.map((p, i) => <p key={i}>{p}</p>)}</div>
          <aside className="rounded-xl bg-white border border-stone-200 p-6 self-start">
            <div data-testid="founder-page-count" className="font-display text-4xl text-[#2B2A26]">{count}</div>
            <div className="text-sm text-stone-500">places I’ve personally visited in this kit</div>
            <div className="font-mono-ui text-sm text-[#9A7B2C] mt-4">${k.price} {k.currency}</div>
            {k.buy_url && <a data-testid="founder-page-buy" href={k.buy_url} className="mt-4 block text-center rounded-sm bg-[#C9A847] text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#B8973A] transition-colors">Buy the full kit</a>}
          </aside>
        </div>
        {k.groups.map((g) => (
          <section key={g.title} className="mb-12" data-testid="founder-page-group">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#9A7B2C] mb-2">{g.title}</div>
            {g.places.map((p) => <Place key={p.name} p={p} i={n++} />)}
          </section>
        ))}
        {k.buy_url && <div className="text-center pt-6"><a data-testid="founder-page-buy-bottom" href={k.buy_url} className="inline-block rounded-sm bg-[#2B2A26] text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-black transition-colors">Get every detail in the {k.title} kit</a></div>}
      </main>
    </div>
  );
}
